// French legal forms and stop words carry no identifying signal, so they're
// dropped before comparing two company names.
const NAME_NOISE_TOKENS = new Set([
  "sa",
  "sas",
  "sasu",
  "sarl",
  "eurl",
  "earl",
  "gaec",
  "scea",
  "sci",
  "snc",
  "scop",
  "association",
  "ets",
  "etablissements",
  "et",
  "de",
  "des",
  "du",
  "la",
  "le",
  "les",
  "aux",
  "the",
]);

/** Significant, accent-folded tokens of a name (legal forms & stop words removed). */
export function significantCompanyNameTokens(
  name: string | null | undefined,
): Set<string> {
  if (!name) return new Set();
  const tokens = name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter((token) => token.length >= 3 && !NAME_NOISE_TOKENS.has(token));
  return new Set(tokens);
}

// A single token shared by two names (a family name common to two unrelated
// franchises, a generic "Distribution"/"Services", ...) is not enough
// evidence that they refer to the same company — most of the cited name's
// significant tokens must be covered by the resolved name's.
const NAME_CONSISTENCY_MIN_OVERLAP_RATIO = 0.6;

/**
 * Whether a company resolved via a discriminator (siren/domain/web search)
 * plausibly refers to the cited name. Used to guard against a directory or
 * aggregator domain/siren the assistant doesn't actually own being attached to
 * several distinct companies, and against a web search's best match being a
 * same-named-but-different company (homonyms, franchises sharing a brand).
 *
 * Requires most of the cited name's significant tokens (see
 * `significantCompanyNameTokens`) to be covered by the resolved name's tokens
 * — not just one — since two unrelated companies can share a single common
 * word while being otherwise unrelated. Conservative at the edges: a cited
 * name with no significant tokens (only legal forms/stop words) or a resolved
 * name with none (e.g. an unenriched discovery stub) has nothing to compare,
 * so the discriminator is trusted rather than rejected.
 *
 * NOTE: kept in sync with the API copy in
 * `api/src/common/utils/company-name-matching.ts`. This pure policy is
 * duplicated on purpose rather than shared through `@shaarp/common`, which
 * stays types-only — putting runtime code there breaks the browser-extension
 * (Plasmo) build.
 */
export function companyNamesAreConsistent(
  citedName: string | null | undefined,
  resolvedName: string | null | undefined,
): boolean {
  const cited = significantCompanyNameTokens(citedName);
  if (cited.size === 0) return true;
  const resolved = significantCompanyNameTokens(resolvedName);
  if (resolved.size === 0) return true;

  const overlap = [...cited].filter((token) => resolved.has(token)).length;
  return overlap / cited.size >= NAME_CONSISTENCY_MIN_OVERLAP_RATIO;
}
